import type { MediaAsset } from '@ghostscreen/contracts';
import { useTranslation } from 'react-i18next';
import { MediaThumb } from '../content/MediaThumb';

type HomePlaylistPreviewProps = {
  previews: MediaAsset[];
  /** Small tiles drop the "No content yet" caption. */
  compact?: boolean;
  /** Size and corner radius come from the caller. */
  className?: string;
};

function EmptyGlyph({ compact }: { compact: boolean }) {
  const { t } = useTranslation('home');
  return (
    <span className="flex flex-col items-center gap-1.5" style={{ color: 'var(--gs-muted)' }}>
      <svg
        width={compact ? 18 : 24}
        height={compact ? 18 : 24}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        aria-hidden
      >
        <path d="m12 3 9 5-9 5-9-5 9-5Z" />
        <path d="m3 13 9 5 9-5" />
      </svg>
      {compact ? null : (
        <span className="text-xs font-semibold">{t('playlistCard.noContent')}</span>
      )}
    </span>
  );
}

/**
 * Real thumbnails of a playlist's first items: one lead tile, plus up to two
 * stacked beside it.
 */
export function HomePlaylistPreview({
  previews,
  compact = false,
  className = '',
}: HomePlaylistPreviewProps) {
  const [lead, ...rest] = previews;

  if (!lead) {
    return (
      <div
        className={`grid place-items-center ${className}`.trim()}
        style={{ background: 'var(--gs-panel2)' }}
      >
        <EmptyGlyph compact={compact} />
      </div>
    );
  }

  return (
    <div className={`flex gap-1 overflow-hidden ${className}`.trim()}>
      <MediaThumb asset={lead} className="h-full min-w-0 flex-[2_1_0%]" />
      {rest.length ? (
        <div className="flex min-w-0 flex-[1_1_0%] flex-col gap-1">
          {rest.map((asset, index) => (
            <MediaThumb key={`${asset.id}-${index}`} asset={asset} className="min-h-0 flex-1" />
          ))}
        </div>
      ) : null}
    </div>
  );
}
